/**
 * Client-side feature flags for the FinTrack dashboard widget redesign (Wave 3).
 *
 * ## Precedence order (highest → lowest)
 *
 * 1. **URL query string** — e.g. `?redesign=v2`. A tester can override
 *    a stored preference without clearing localStorage, and deep links
 *    are explicit.
 * 2. **localStorage** — persistent per-browser opt-in, written by the
 *    Settings page toggle.
 * 3. **Default** — `false`. All flags are **opt-in**: no user is
 *    auto-enrolled, and there is no server-side rollout.
 *
 * ## Plan / scope
 *
 * - This module is intentionally pure / synchronous. It has **no
 *   React state of its own**; consumers that need to re-read on
 *   toggle changes should wrap the call in `useMemo` (see
 *   `Settings.tsx`).
 * - There is **no backend endpoint**. The flag is local + URL only.
 * - See the Wave 3 / T19 entry in `docs/CHANGELOG.md` for context.
 *
 * @see frontend/src/components/FeatureFlagBoundary.tsx
 * @see frontend/src/pages/Settings.tsx (Appearance → "Enable new dashboard design")
 */

/** All feature flags currently recognised by the FinTrack frontend. */
export type FeatureFlag = 'redesign-v2' | 'day-of-week-server-filter';

/** localStorage key for each flag. Kept as a const map for type safety. */
const FLAG_LOCALSTORAGE_KEYS: Record<FeatureFlag, string> = {
  'redesign-v2': 'fintrack-redesign-v2',
  'day-of-week-server-filter': 'fintrack-day-of-week-server-filter',
};

/** URL query parameter name for each flag. */
const FLAG_URL_PARAMS: Record<FeatureFlag, string> = {
  'redesign-v2': 'redesign',
  'day-of-week-server-filter': 'day-of-week-server-filter',
};

/**
 * The exact URL parameter value that enables a flag.
 *
 * The URL check is value-equal (not just presence-based) so a tester
 * can explicitly **disable** a flag via `?redesign=off` even if the
 * user has `fintrack-redesign-v2 = "true"` in localStorage.
 */
const FLAG_URL_ENABLE_VALUES: Record<FeatureFlag, string> = {
  'redesign-v2': 'v2',
  'day-of-week-server-filter': 'true',
};

/**
 * Returns true when the given flag is enabled for the current browser
 * context, following the precedence documented at the top of this file.
 *
 * Safe to call during SSR — returns `false` when `window` is
 * undefined, so server-rendered markup matches the default (off)
 * state. Hydration may flip the value on the client; opt-in flags
 * default off, so this is benign.
 *
 * @param flag The feature flag to check.
 */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  if (typeof window === 'undefined') return false;

  // 1. URL query string — highest priority.
  try {
    const url = new URL(window.location.href);
    const urlValue = url.searchParams.get(FLAG_URL_PARAMS[flag]);
    if (urlValue != null) {
      return urlValue === FLAG_URL_ENABLE_VALUES[flag];
    }
  } catch {
    // Malformed URL — fall through to localStorage.
  }

  // 2. localStorage — persistent opt-in.
  try {
    const lsValue = window.localStorage.getItem(FLAG_LOCALSTORAGE_KEYS[flag]);
    return lsValue === 'true';
  } catch {
    // localStorage may be disabled (private mode, quota, etc.)
    return false;
  }
}
